import { HttpClient } from "@angular/common/http";
import { Inject, Injectable, signal } from "@angular/core";
import { Router } from "@angular/router";
import * as url from '@shared/constants/login-urls';
import { JsonResponse } from "@shared/interfaces/JsonResponse.interface";
import {
    clearAccessTokenCookie,
    getAccessTokenCookie,
    getCurrentPlatformCookiePath
} from "@shared/utils/cookie.utils";
import Keycloak from 'keycloak-js';
import { LocalStorageService, SessionStorageService } from "ngx-webstorage";
import { catchError, map, Observable, of, tap } from "rxjs";
import { ToasterService } from "./toaster.service";
import { LabService } from "./lab.service";
import {AndroidService} from "@shared/services/android.service";

export type LoginType = 'companyPersonnel' | 'examinee' | 'samplePartner' | 'doctorView';

@Injectable({
    providedIn: 'root'
})
export class AuthService {

    private _accessGranted: boolean = false;
    private _authenticated: boolean = false;
    private _doctorViewAuthenticated: boolean = false;

    private _keycloak: Keycloak = null;

    public environment: any = null
    public isAuthenticated = signal<boolean>(false)

    constructor(
        @Inject('environment') private _environment: any,
        private _router: Router,
        private _http: HttpClient,
        private _localStorageService: LocalStorageService,
        private _sessionStorageService: SessionStorageService,
        private _toasterService: ToasterService,
        private _androidService: AndroidService,
        private _labService: LabService
    ) {

        this.authenticated = !!(this._localStorageService.retrieve('session_token') || this._sessionStorageService.retrieve('session_token'));
        this.doctorViewAuthenticated = !!this._localStorageService.retrieve('doctor_view_session_token');
        this._accessGranted = !!getAccessTokenCookie();

        this.environment = this._environment;
        this._androidService.getVersion();
    }

    get authenticated() {
        return this._authenticated;
    }

    set authenticated(authenticated: boolean) {
        this._authenticated = authenticated;
        this.isAuthenticated.set(authenticated);
    }

    get doctorViewAuthenticated() {
        return this._doctorViewAuthenticated;
    }

    set doctorViewAuthenticated(authenticated: boolean) {
        this._doctorViewAuthenticated = authenticated;
    }

    get accessGranted(): boolean {
        return this._accessGranted
    }

    set accessGranted(granted: boolean) {
        this._accessGranted = granted;
    }

    public resetAccess(redirectToLogin: boolean = true): void {
        clearAccessTokenCookie(getCurrentPlatformCookiePath());
        this._accessGranted = false;

        if (redirectToLogin) {
            this.redirectToLogin();
        }
    }

    public sendCode(prefix: string, phoneNumber: string, type: LoginType = 'companyPersonnel'): Observable<JsonResponse<any>> {
        return this._http.post<any>('/login/send_code', { prefix, phoneNumber, type }).pipe(
            catchError(err => {
                return of({
                    success: false,
                    error: err.error,
                    message: err.error?.message
                });
            }),
        );
    }

    public verifyCode(code: string, userId: string, getLogo: boolean, type: LoginType = "companyPersonnel"): Observable<JsonResponse<any>> {
        return this._http.post<any>('/login/verify', { code, userId, getLogo, type }).pipe(
            catchError(err => {
                return of({
                    success: false,
                    error: err.error,
                    message: err.error?.message
                });
            }),
            tap(res => {
                if (res.success) {
                    if (this._isEndUser()) {
                        this._sessionStorageService.store('session_token', res.data.token);

                    } else {
                        if (type === 'doctorView') {
                            this._localStorageService.store('doctor_view_session_token', res.data.token);

                            this._doctorViewAuthenticated = true;
                        } else {
                            this._localStorageService.store('session_token', res.data.token);
                        }
                    }

                    this.authenticated = true;

                    if (res.data?.companyLogo) {
                        this._localStorageService.store("logo", res.data.companyLogo);
                    }
                }
            })
        );
    }

    public async checkRe18Login() {

        this._keycloak = new Keycloak({
            url: "https://keycloak.re18.tech",
            realm: "Re18",
            clientId: this._environment.project
        });

        const ssoAuthenticated = await this._keycloak.init({
            checkLoginIframe: false,
            flow: 'hybrid'
        });

        if (ssoAuthenticated) {
            const params = {
                "access_token": this._keycloak.token,
                "id": this._keycloak.idTokenParsed.sub,
                ...this._keycloak.tokenParsed
            }

            this._http.post<JsonResponse<any>>('/re18-login', {...params}).subscribe(res => {

                if (!res.success) {
                    this._toasterService.error('error', res.message);
                } else {

                    this._localStorageService.store('session_token', res.data.token);

                    this.authenticated = true;

                    this._router.navigate(['/']);
                }
            })

        }
    }

    public loginWithRe18(): void {
        this._keycloak.login({
            "redirectUri": this._environment.siteUrl + this._environment.loginWithRevolabUrl
        })
    }

    public logout(redirectUrl?: string): void {

        this._http.post<JsonResponse<any>>('/login/logout', {}).subscribe(res => {
            if (!res.success) {
                this._toasterService.error('error', res.message);
            } else {

                this._clearLocalStorageAndRedirectToLogin(redirectUrl);
            }
        });

    }

    private _clearLocalStorageAndRedirectToLogin(redirectPath?: string): void {
        //preserve these from deletion
        const installation_id = this._localStorageService.retrieve('installation_id');
        const defaultReceiptsPrinter = this._localStorageService.retrieve('defaultReceiptsPrinter');
        const defaultResultsPrinter = this._localStorageService.retrieve('defaultResultsPrinter');
        const thermalPrinters = this._localStorageService.retrieve('thermalPrinters') || [];
        const logo = this._localStorageService.retrieve("logo");
        const lng = this._localStorageService.retrieve('lng');

        let orderCode: string;

        if (this._isEndUser()) {
            orderCode = this._sessionStorageService.retrieve('user_order');
        }

        this._localStorageService.clear();
        this._sessionStorageService.clear();
        clearAccessTokenCookie(getCurrentPlatformCookiePath());

        this._localStorageService.store('installation_id', installation_id);
        this._localStorageService.store('defaultReceiptsPrinter', defaultReceiptsPrinter);
        this._localStorageService.store('defaultResultsPrinter', defaultResultsPrinter);
        this._localStorageService.store('thermalPrinters', thermalPrinters);

        if (lng) {
            this._localStorageService.store('lng', lng);
        }

        if (logo) {
            this._localStorageService.store("logo", logo);
        }

        if (this._isEndUser()) {
            this._sessionStorageService.store('user_order', orderCode);
        }

        this.authenticated = false;
        this._accessGranted = false;

        this.redirectToLogin(redirectPath, true);
    }

    public redirectToLogin(redirectPath?: string, clearLogin?: boolean): void {

        let loginUrl;

        if (this._environment.name === 'production') {
            loginUrl = url.PROD_LOGIN_URL;
        } else if (this._environment.name === 'development') {
            loginUrl = url.DEV_LOGIN_URL;
        } else {
            loginUrl = url.LOCAL_LOGIN_URL;
        }

        if (redirectPath) {

            const {slug} = this._labService.labData ?? {};

            loginUrl += `?redirectPlatform=${this._environment.slug}&redirectPath=${redirectPath}`;

            if (slug) {
                loginUrl += `&lab=${slug}`;
            }
        }

        if (clearLogin) {
            loginUrl += redirectPath ? `&clearLogin=true` : `?clearLogin=true`;
        }

        window.location.href = loginUrl;
    }

    private _isEndUser() {
        return this._environment.slug === "user-results";
    }

    public getWsTicket(sessionToken: string, installationId: string): Observable<string> {
        return this._http.post<{ ticket: string }>(
            `${this._environment.wsHttpEndpoint}/ws/ticket`,
            { sessionToken, installationId },
            { headers: { skip: 'true' } }
        ).pipe(
            map(res => {
                if (!res?.ticket) {
                    throw new Error('Failed to obtain WS ticket');
                }
                return res.ticket;
            })
        );
    }

    public getCompanyLogo(slug: string): Observable<JsonResponse<string>> {
        return this._http.post<JsonResponse<string>>(`/login/get-company-logo`, { slug })
            .pipe(tap(res => {
                if (!res.success) {
                    this._toasterService.error('error', res.message);
                }
            }))
    }

}
